import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Noop } from 'types/common.interface';

import { IState } from 'models/reducers/i-state.interface';
import { IWalletReducer } from 'models/reducers/i-wallet-reducer.interface';

import { ROUTES } from 'constants/routes.constant';

import vipPoint from 'assets/vip_point.png';
import rmv from 'assets/wheel-of-fortune/rmv.png';

import { RewardKind } from '../WheelOfFortune/types';

/** One prize as this view paints it: an amount, an icon and a name. */
export interface IWheelOfFortuneResultRow {
  reward: string;
  rewardKind?: RewardKind;
  prizeName?: string;
  iconUrl?: string;
}

interface IWheelOfFortuneResultProps {
  reward: string;
  rewardKind?: RewardKind;
  /** Full prize label from the API, preferred over the generic per-kind label. */
  prizeName?: string;
  iconUrl?: string;
  /**
   * Every prize of a batch spin, in spin order. When given it REPLACES the single
   * `reward`/`rewardKind`/... prize above — those stay the props the 1x spin and the
   * "here is what you won last time" teaser pass, and neither of those has a list.
   *
   * This must be built from what actually committed (`results.length`), never from what was
   * requested: a short batch grants fewer than it set out to, and rendering the request
   * would show the holder prizes they did not win.
   */
  rows?: IWheelOfFortuneResultRow[];
  /**
   * Spins this batch consumed. Shown alongside the prizes so the modal answers "what
   * happened" without a trip to the history table — a batch that paid out "Try Again" hands
   * spins BACK, so the balance on screen afterwards does not say what was spent.
   */
  spinsUsed?: number;
  /** Accent for the actions, so they follow whichever wheel is showing. */
  accentClass?: string;
  onClose: Noop;
  hasAvailableSpin?: boolean;
  onSpinAgain?: Noop;
}

/** Generic fallback labels, used when the API did not name the prize. */
const KIND_LABEL: Record<RewardKind, string> = {
  rmv: 'RMV',
  vip_points: 'VIP Points',
  wof_spins: 'Spins',
  vip_wof_spins: 'VIP Spins',
  lootbox: 'Lootbox',
  nft: 'NFT',
};

const iconFor = (row: IWheelOfFortuneResultRow) => row.iconUrl || (row.rewardKind === 'vip_points' ? vipPoint : rmv);

const labelFor = (row: IWheelOfFortuneResultRow) => row.prizeName || KIND_LABEL[row.rewardKind ?? 'rmv'];

const WheelOfFortuneResult = ({
  reward,
  rewardKind = 'rmv',
  prizeName,
  iconUrl,
  rows,
  spinsUsed,
  accentClass = 'wof-btn--primary',
  onClose,
  hasAvailableSpin = false,
  onSpinAgain,
}: IWheelOfFortuneResultProps) => {
  const { assets } = useSelector(({ wallet }: IState): IWalletReducer => wallet);

  // One code path for both shapes: a 1x spin is a batch of one built from the scalar props.
  const items: IWheelOfFortuneResultRow[] = rows?.length ? rows : [{ reward, rewardKind, prizeName, iconUrl }];
  const isBatch = items.length > 1;

  // Withdrawing and the safe only make sense for RMV; every other prize is credited to a
  // balance and has nothing to withdraw. A batch qualifies if ANY of its prizes was RMV —
  // hiding the withdraw route because the tenth spin paid VIP points would strand the RMV
  // won on the other nine.
  const showWithdrawFlow = items.some((item) => (item.rewardKind ?? 'rmv') === 'rmv');
  const hasAssets = Boolean(assets?.length);

  return (
    <div
      className="tw-absolute tw-inset-x-0 tw-top-1/2 tw-z-50 tw-flex tw-flex-col tw-items-center tw-gap-7.5 tw-bg-overlayGradient tw-px-9 tw-py-[24px]"
      style={{ transform: 'translateY(-50%)' }}
    >
      <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-text-center tw-text-xl tw-font-semibold md:tw-text-2xl">
        {isBatch ? (
          <>
            <p className="tw-mb-[6px] tw-uppercase">
              You win <span className="tw-text-green">{items.length}</span> prizes
            </p>
            {/* What the batch cost, next to what it won — see `spinsUsed`. */}
            {spinsUsed !== undefined && (
              <p className="tw-mb-[12px] tw-text-sm tw-font-normal tw-opacity-70" data-testid="result-cost">
                {spinsUsed} {spinsUsed === 1 ? 'spin' : 'spins'} spent
              </p>
            )}
            {/* The overlay sits on top of the wheel and the modal itself never scrolls, so
                this list is the one thing here allowed a scrollbar — ten rows do not fit. */}
            <ul className="wof-result-list" data-testid="result-rows">
              {items.map((item, index) => (
                <li
                  // Nothing in a batch is unique: the same prize can be won twice and the
                  // result ids are not carried this far. Position in spin order is the
                  // identity, and this list is never reordered, filtered or appended to.
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className="wof-result-list__row"
                >
                  <span className="wof-result-list__name">{labelFor(item)}</span>
                  <span className="wof-result-list__amount">
                    <span className="tw-text-green">{parseFloat(item.reward)}</span>
                    <img src={iconFor(item)} alt={labelFor(item)} className="tw-h-5 tw-w-5" />
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="tw-mb-[18px] tw-flex tw-items-center tw-gap-1 tw-uppercase">
            You win <span className="tw-text-green">{parseFloat(items[0].reward)}</span>
            <img src={iconFor(items[0])} alt={labelFor(items[0])} className="tw-h-7 tw-w-7" />
          </p>
        )}
        {showWithdrawFlow && !hasAssets ? (
          <p className="tw-mt-[10px] tw-text-sm tw-font-normal">
            <span className="tw-text-green">Buy NFT</span> or collect 1000 $RMV to{' '}
            <span className="tw-text-green">withdraw now!</span>
          </p>
        ) : (
          <span className="tw-mt-[10px] tw-text-[18px] tw-font-bold tw-leading-[80%]">
            {showWithdrawFlow
              ? 'The Reward will be available tomorrow with Royalties.'
              : 'The Reward has been added to your balance.'}
          </span>
        )}
      </div>
      <div className="tw-flex tw-flex-row tw-gap-[20px]">
        {showWithdrawFlow && !hasAssets && (
          <Link
            to={ROUTES.LOOTBOX}
            aria-label="Buy NFT"
            onClick={onClose}
            className="wof-btn wof-btn--ghost tw-w-36 tw-no-underline"
          >
            Buy NFT
          </Link>
        )}
        {showWithdrawFlow &&
          (hasAssets ? (
            <Link
              to={`${ROUTES.PROFILE}/collected`}
              aria-label="Withdraw"
              onClick={onClose}
              className={`wof-btn ${accentClass} tw-w-36 tw-no-underline`}
            >
              Withdraw
            </Link>
          ) : (
            // A disabled <a> is still clickable, so render a real disabled button when
            // there is nothing to withdraw rather than a link that only looks inert.
            <button type="button" disabled aria-label="Withdraw" className={`wof-btn ${accentClass} tw-w-36`}>
              Withdraw
            </button>
          ))}
        {hasAvailableSpin && onSpinAgain && (
          <button
            type="button"
            aria-label="Spin again"
            onClick={onSpinAgain}
            className={`wof-btn ${accentClass} tw-w-36`}
          >
            Spin again
          </button>
        )}
      </div>
    </div>
  );
};

export default WheelOfFortuneResult;
